import { evaluateRiskForecast, type StateOfSalesV2LossSignalGroup } from "../lib/risk";
import { getCompanyTarget } from "../lib/companies";

type ForecastArgs = Parameters<typeof evaluateRiskForecast>;
type LossInput = { deals: ForecastArgs[0]; companyId: number; pipelines: ForecastArgs[2];
  currency: ForecastArgs[8]; stageTimes: NonNullable<ForecastArgs[9]>["dealstageAvgTimes"] };

/** Reuse the public evaluator's unchanged last-30-days loss output. The current
 * and previous calendar quarters are disjoint and together cover that window,
 * including January. Ignore every Forecast/target output; never shift dates,
 * access the private helper, rescore signals here or read the user's period.
 */
export function evaluateRecentLossGroups({ deals, companyId, pipelines, currency, stageTimes }: LossInput) {
  const frames = (["prev_quarter", "this_quarter"] as const).map(period => evaluateRiskForecast(
    structuredClone(deals), companyId, pipelines, period, "all", "all", getCompanyTarget(companyId), false, currency,
    { includeL2Details: true, includeOpenDeals: false, movementDeals: [], dealstageAvgTimes: stageTimes },
  ).stateOfSalesV2LossSignalGroups);
  return combineRecentLossGroups(frames[0], frames[1]);
}

/** Presentation union of evaluated outputs, not a second loss/signal model. */
export function combineRecentLossGroups(previous: StateOfSalesV2LossSignalGroup[], current: StateOfSalesV2LossSignalGroup[]) {
  const priorIds = new Set(previous.flatMap(group => group.deals.map(deal => String(deal.id))));
  if (current.some(group => group.deals.some(deal => priorIds.has(String(deal.id))))) {
    throw new Error("Recent-loss quarter populations overlap.");
  }
  if (previous.length !== 3 || current.length !== 3 || new Set(previous.map(group => group.group)).size !== 3
    || new Set(current.map(group => group.group)).size !== 3) throw new Error("Recent-loss outputs are incomplete.");
  return current.map(group => {
    const prior = previous.find(row => row.group === group.group);
    if (!prior) throw new Error("Recent-loss groups do not match.");
    return { ...group, affectedDealCount: prior.affectedDealCount + group.affectedDealCount,
      historicallyEvaluatedDealCount: prior.historicallyEvaluatedDealCount + group.historicallyEvaluatedDealCount,
      missingHistoricalDealCount: prior.missingHistoricalDealCount + group.missingHistoricalDealCount,
      recentLostDealCount: prior.recentLostDealCount + group.recentLostDealCount,
      deals: [...prior.deals, ...group.deals] };
  });
}
